import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  MessageCircle, Send, X, Loader2, User, ShieldCheck, ArrowLeft, ExternalLink,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

const THREAD_KEY_STORAGE = 'sprite-vault:support-thread-key:v1';
const ANON_NAME_STORAGE = 'sprite-vault:support-anon-name:v1';
const ANON_EMAIL_STORAGE = 'sprite-vault:support-anon-email:v1';
const POLL_MS = 15000;

// TODO: swap these placeholders for the real Sprite Vault Discord invite &
// Facebook Page m.me link when they exist.
const DISCORD_URL = 'https://discord.gg/spritevault';
const MESSENGER_URL = 'https://m.me/spritevault';

function DiscordIcon({ className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.211.375-.444.865-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.68 12.68 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.045-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.077.077 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.105 13.106 13.106 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.121.098.246.197.372.29a.077.077 0 0 1-.006.129 12.298 12.298 0 0 1-1.873.891.077.077 0 0 0-.041.106c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.548-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.211 0 2.176 1.094 2.157 2.418 0 1.334-.955 2.419-2.157 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.211 0 2.176 1.094 2.157 2.418 0 1.334-.946 2.419-2.157 2.419z"/>
    </svg>
  );
}

function MessengerIcon({ className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.14.26.34.27.55l.05 1.78c.02.57.6.94 1.13.72l1.98-.87c.16-.07.34-.08.5-.04 1 .27 2.05.41 3.13.41 5.64 0 10-4.13 10-9.72S17.64 2 12 2zm6.01 7.63l-2.94 4.66a1.5 1.5 0 0 1-2.16.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.4c-.42.32-.97-.18-.7-.63l2.94-4.67a1.5 1.5 0 0 1 2.16-.4l2.34 1.75c.21.16.51.16.72 0l3.16-2.39c.42-.32.97.19.7.63z"/>
    </svg>
  );
}

function getAnonThreadKey() {
  try {
    let k = localStorage.getItem(THREAD_KEY_STORAGE);
    if (!k) {
      k = `anon:${(crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now().toString(36)}`;
      localStorage.setItem(THREAD_KEY_STORAGE, k);
    }
    return k;
  } catch {
    return null;
  }
}

export default function ChatBubble() {
  const { isAuthed, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('menu'); // 'menu' | 'direct'
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [anonName, setAnonName] = useState('');
  const [anonEmail, setAnonEmail] = useState('');
  const listRef = useRef(null);
  const pollRef = useRef(null);

  // Hydrate anon contact info from localStorage
  useEffect(() => {
    try {
      setAnonName(localStorage.getItem(ANON_NAME_STORAGE) || '');
      setAnonEmail(localStorage.getItem(ANON_EMAIL_STORAGE) || '');
    } catch { /* ignore */ }
  }, []);

  const currentThreadKey = useCallback(() => {
    if (isAuthed && user?.id) return `user:${user.id}`;
    return getAnonThreadKey();
  }, [isAuthed, user]);

  const loadMessages = useCallback(async () => {
    try {
      const tk = currentThreadKey();
      const msgs = await api.listSupportMessages(isAuthed ? undefined : tk);
      setMessages(msgs || []);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, [currentThreadKey, isAuthed]);

  // Load on entering direct-chat view + start polling for admin replies
  useEffect(() => {
    if (!open || view !== 'direct') return;
    loadMessages();
    pollRef.current = setInterval(loadMessages, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, view, loadMessages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (view === 'direct' && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, view]);

  // Reset to menu view whenever the drawer opens
  useEffect(() => {
    if (open) setView('menu');
  }, [open]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      const payload = { text: value };
      if (!isAuthed) {
        const tk = getAnonThreadKey();
        if (tk) payload.thread_key = tk;
        if (anonName.trim()) {
          payload.name = anonName.trim();
          try { localStorage.setItem(ANON_NAME_STORAGE, anonName.trim()); } catch { /* ignore */ }
        }
        if (anonEmail.trim()) {
          payload.email = anonEmail.trim();
          try { localStorage.setItem(ANON_EMAIL_STORAGE, anonEmail.trim()); } catch { /* ignore */ }
        }
      }
      const { message, thread_key } = await api.sendSupportMessage(payload);
      if (!isAuthed && thread_key) {
        try { localStorage.setItem(THREAD_KEY_STORAGE, thread_key); } catch { /* ignore */ }
      }
      setMessages((prev) => [...prev, message]);
      setText('');
    } catch (e) {
      toast({ title: 'Could not send', description: e.message || 'Try again in a moment.' });
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          data-testid="chat-bubble-btn"
          aria-label="Chat with the devs"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 z-40 h-14 w-14 rounded-full bg-[#ff2bd6] hover:bg-[#e119bf] text-white border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064,0_0_24px_rgba(255,43,214,0.5)] flex items-center justify-center transition-transform hover:-translate-y-0.5 active:translate-y-0"
        >
          <MessageCircle className="h-6 w-6" strokeWidth={2.5} />
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          data-testid="chat-bubble-panel"
          className="fixed bottom-3 left-3 z-40 w-[calc(100vw-24px)] sm:w-[380px] max-h-[calc(100vh-24px)] flex flex-col rounded-2xl bg-white border-[3px] border-[#3a1064] shadow-[0_8px_0_#3a1064,0_24px_60px_-12px_rgba(0,0,0,0.4)] overflow-hidden"
        >
          {/* Header */}
          <div
            className="p-3 sm:p-4 border-b-[3px] border-[#3a1064] flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg,#ffd900,#ff2bd6 60%,#9d3dff)' }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {view === 'direct' ? (
                <button
                  data-testid="chat-bubble-back-btn"
                  aria-label="Back to chat options"
                  onClick={() => setView('menu')}
                  className="h-9 w-9 rounded-xl bg-white border-2 border-[#3a1064] text-[#3a1064] shadow-[0_2px_0_#3a1064] hover:bg-yellow-50 flex items-center justify-center shrink-0"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={3} />
                </button>
              ) : (
                <div className="h-9 w-9 rounded-xl bg-white border-2 border-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064] shrink-0 -rotate-6">
                  <MessageCircle className="h-5 w-5 text-[#ff2bd6]" strokeWidth={3} />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-white font-black text-base sm:text-lg leading-tight drop-shadow-[0_2px_0_#3a1064]">
                  {view === 'direct' ? 'Chat with the Devs' : 'How can we help?'}
                </div>
                <div className="text-white/90 font-bold text-[10px] sm:text-xs">
                  {view === 'direct' ? 'Usually reply within 24h' : 'Pick a channel to reach us'}
                </div>
              </div>
            </div>
            <button
              data-testid="chat-bubble-close-btn"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-xl bg-white border-2 border-[#3a1064] text-[#3a1064] shadow-[0_2px_0_#3a1064] hover:bg-yellow-50 flex items-center justify-center shrink-0"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>

          {view === 'menu' && (
            <div className="p-4 space-y-2.5 bg-[#fff7d6]">
              <a
                data-testid="chat-option-discord"
                href={DISCORD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border-[3px] border-[#3a1064] bg-[#5865F2] hover:bg-[#4752c4] shadow-[0_4px_0_#3a1064] px-4 py-3 transition-transform hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="h-10 w-10 rounded-lg bg-white border-2 border-[#3a1064] flex items-center justify-center shrink-0 text-[#5865F2]">
                  <DiscordIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 text-white">
                  <div className="font-black text-base leading-tight">Join our Discord</div>
                  <div className="font-bold text-[11px] opacity-90">Chat with the whole community in real time</div>
                </div>
                <ExternalLink className="h-4 w-4 text-white shrink-0" strokeWidth={3} />
              </a>

              <a
                data-testid="chat-option-messenger"
                href={MESSENGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-xl border-[3px] border-[#3a1064] bg-[#0084ff] hover:bg-[#006edb] shadow-[0_4px_0_#3a1064] px-4 py-3 transition-transform hover:-translate-y-0.5 active:translate-y-0"
              >
                <div className="h-10 w-10 rounded-lg bg-white border-2 border-[#3a1064] flex items-center justify-center shrink-0 text-[#0084ff]">
                  <MessengerIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 text-white">
                  <div className="font-black text-base leading-tight">Facebook Messenger</div>
                  <div className="font-bold text-[11px] opacity-90">DM our page — replies land in your inbox</div>
                </div>
                <ExternalLink className="h-4 w-4 text-white shrink-0" strokeWidth={3} />
              </a>

              <button
                data-testid="chat-option-direct"
                onClick={() => setView('direct')}
                className="w-full flex items-center gap-3 rounded-xl border-[3px] border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] shadow-[0_4px_0_#3a1064] px-4 py-3 transition-transform hover:-translate-y-0.5 active:translate-y-0 text-left"
              >
                <div className="h-10 w-10 rounded-lg bg-white border-2 border-[#3a1064] flex items-center justify-center shrink-0 text-[#ff2bd6]">
                  <MessageCircle className="h-5 w-5" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1 text-white">
                  <div className="font-black text-base leading-tight">Message the Devs Directly</div>
                  <div className="font-bold text-[11px] opacity-90">
                    {isAuthed
                      ? 'Private thread — replies show up right here'
                      : 'Anonymous — add your email so we can reply'}
                  </div>
                </div>
              </button>

              <p className="text-[10px] font-bold text-[#3a1064]/60 text-center pt-1">
                Bug reports, trade issues, feature ideas — anything goes.
              </p>
            </div>
          )}

          {view === 'direct' && (
          <>
          {/* Message list */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#fff7d6]"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {!loaded && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 text-[#9d3dff] animate-spin" />
              </div>
            )}
            {loaded && messages.length === 0 && (
              <div className="text-center py-6 px-2">
                <MessageCircle className="h-10 w-10 mx-auto text-[#ff2bd6]" />
                <div className="mt-2 font-black text-[#3a1064]">Say hi!</div>
                <div className="text-xs font-bold text-[#3a1064]/70 mt-1">
                  Bug reports, trade issues, feature ideas — anything goes. The devs read every message.
                </div>
              </div>
            )}
            {messages.map((m) => {
              const isAdmin = m.sender_type === 'admin';
              return (
                <div
                  key={m.id}
                  className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 border-2 shadow-[0_2px_0_#3a1064] text-sm font-bold whitespace-pre-wrap break-words ${
                      isAdmin
                        ? 'bg-white text-[#3a1064] border-[#3a1064] rounded-tl-sm'
                        : 'bg-[#ff2bd6] text-white border-[#3a1064] rounded-tr-sm'
                    }`}
                  >
                    <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-wider mb-0.5 ${isAdmin ? 'text-[#9d3dff]' : 'text-yellow-200'}`}>
                      {isAdmin ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {isAdmin ? 'Dev Team' : m.sender_name || 'You'}
                    </div>
                    <div>{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Anon contact info */}
          {!isAuthed && (
            <div className="px-3 pt-2 border-t-2 border-[#3a1064]/15 bg-yellow-50/60 grid grid-cols-2 gap-2">
              <input
                data-testid="chat-anon-name-input"
                value={anonName}
                onChange={(e) => setAnonName(e.target.value)}
                placeholder="Your name (optional)"
                className="h-8 rounded-lg border-2 border-[#3a1064]/40 bg-white px-2 text-xs font-bold text-[#3a1064] focus:outline-none focus:border-[#ff2bd6]"
                maxLength={80}
              />
              <input
                data-testid="chat-anon-email-input"
                value={anonEmail}
                onChange={(e) => setAnonEmail(e.target.value)}
                placeholder="Email (so we can reply)"
                type="email"
                className="h-8 rounded-lg border-2 border-[#3a1064]/40 bg-white px-2 text-xs font-bold text-[#3a1064] focus:outline-none focus:border-[#ff2bd6]"
                maxLength={150}
              />
            </div>
          )}

          {/* Composer */}
          <div className="p-2 sm:p-3 border-t-[3px] border-[#3a1064] bg-white flex items-end gap-2">
            <textarea
              data-testid="chat-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="flex-1 min-h-[38px] max-h-[100px] rounded-xl border-2 border-[#3a1064] px-3 py-2 font-bold text-[#3a1064] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#ff2bd6]/40"
              maxLength={2000}
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={sending || !text.trim()}
              aria-label="Send message"
              className="h-10 w-10 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] disabled:opacity-40 text-white shadow-[0_3px_0_#3a1064] flex items-center justify-center shrink-0"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={3} />}
            </button>
          </div>
          </>
          )}
        </div>
      )}
    </>
  );
}
